import { createSelector } from 'reselect';
import { AppState } from '../../model';
import { OptionId, UpdateSegmentRequest } from './../../../models/itinerary';

/* -------------------------------------------------------------------------- */
/*                              Selected options                              */
/* -------------------------------------------------------------------------- */

/**
 * Get list of all selected options
 */
export const getSelectedOptions = ({ selectedOptions }: AppState) =>
  selectedOptions;

/**
 * Find selected option by segment id
 */
export const findSelectedOption = (segmentId: number) =>
  createSelector(
    getSelectedOptions,
    (selectedOptions): UpdateSegmentRequest =>
      Object.values(selectedOptions)[segmentId],
  );

/**
 * Check if an option has been selected
 */
export const isSelected = (segmentId: number, optionId: OptionId) =>
  createSelector(
    findSelectedOption(segmentId),
    ({ airSegment, hotelSegment, carSegment, segmentType }): boolean => {
      switch (segmentType) {
        case 'Air':
          return airSegment!.flightId === Number(optionId);
        case 'Hotel':
          return hotelSegment!.hotelId === Number(optionId);
        case 'Car':
          return carSegment!.supplierCode === String(optionId);
      }
    },
  );

/**
 * Check if any alternative options have been chosen by the traveller
 */
export const hasChosenAlternatives = createSelector(
  getSelectedOptions,
  (selectedOptions): boolean =>
    Object.values(selectedOptions).some(
      ({ recommendedSelected }) => !recommendedSelected,
    ),
);

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

/**
 * Determine option id given a selected option
 */
export const getOptionIdFromOption = (option: UpdateSegmentRequest) => {
  switch (option.segmentType) {
    case 'Air':
      return option.airSegment!.flightId.toString();
    case 'Hotel':
      return option.hotelSegment!.hotelId.toString();
    case 'Car':
      return option.carSegment!.supplierCode;
  }
};

/* -------------------------------------------------------------------------- */
/*                              Selected combinations                              */
/* -------------------------------------------------------------------------- */

/**
 * Get list of all selected combinations
 */
export const getSelectedCombinations = ({ itinerary }: AppState) =>
  itinerary.data.selectedCombinations;
